import React, { useCallback, useRef, useEffect } from 'react';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';

function useClickOutside(ref, onClickOutside, ignoreTarget) {
  const clickCapturedRef = useRef(false);
  useEffect(() => {
    function handleClickOutside(evt) {
      // The element that opened us is not "outside": it toggles on click, and closing here on mousedown
      // would make that click reopen what we just closed.
      const ignored = ignoreTarget && ignoreTarget();
      const isIgnored = ignored && ignored.contains && ignored.contains(evt.target);
      if (!clickCapturedRef.current && !isIgnored && ref.current && !ref.current.contains(evt.target)) {
        onClickOutside && onClickOutside(evt);
      }
      clickCapturedRef.current = false;
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, clickCapturedRef, onClickOutside, ignoreTarget]);

  return useCallback(() => {
    clickCapturedRef.current = true;
  }, []);
}

function OutsideClickHandler({
  children,
  onClickOutside = noop,
  disableOnClickOutside = false,
  ignoreTarget,
  innerRef,
}) {
  const ref = useRef(null);
  const actualRef = innerRef || ref;
  const captureInnerClick = useClickOutside(actualRef, onClickOutside, ignoreTarget);

  if (innerRef || disableOnClickOutside) {
    return children;
  }

  return (
    <div onMouseDown={captureInnerClick} onTouchStart={captureInnerClick} ref={actualRef}>
      {children}
    </div>
  );
}

OutsideClickHandler.propTypes = {
  children: PropTypes.node.isRequired,
  innerRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  onClickOutside: PropTypes.func,
  disableOnClickOutside: PropTypes.bool,
  ignoreTarget: PropTypes.func,
};

export default OutsideClickHandler;
